import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '12px', 
      minWidth: '500px'
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '24px'
  },
  actions: {
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0'
  },
  submitButton: {
    backgroundColor: 'primary.main',
    borderRadius: '8px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#333'
    }
  },
  cancelButton: {
    color: '#666',
    borderRadius: '8px',
    textTransform: 'none'
  }
};

function Form({ open, onClose, itemData = null, onSubmit }) {
  const [formData, setFormData] = React.useState({
    name: '',
  });
  const [errors, setErrors] = React.useState({});
  const [generalError, setGeneralError] = React.useState(null);

  React.useEffect(() => {
    if (itemData) {
      setFormData({
        name: itemData.name || '',
      });
    } else {
      setFormData({
        name: '',
      });
    }
    // Réinitialiser les erreurs quand le modal s'ouvre ou que les données changent
    setErrors({});
    setGeneralError(null);
  }, [itemData, open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Effacer l'erreur du champ quand l'utilisateur commence à taper
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    // Réinitialiser les erreurs
    setErrors({});
    setGeneralError(null);
    
    // Créer un callback pour recevoir les erreurs
    const errorCallback = (validationErrors) => {
      const errorsObj = {};
      let generalMessage = null;
      
      if (validationErrors && validationErrors.length > 0) {
        validationErrors.forEach(error => {
          if (error.field) {
            errorsObj[error.field] = error.message;
          } else {
            generalMessage = error.message || 'Une erreur est survenue';
          }
        });
      }
      
      setErrors(errorsObj);
      setGeneralError(generalMessage);
    };
    
    try {
      if (onSubmit) {
        await onSubmit(formData, errorCallback);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
    });
    setErrors({});
    setGeneralError(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      sx={styles.dialog}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <span>{itemData ? 'Modifier le type de ticket' : 'Nouveau type de ticket'}</span>
        <IconButton 
          onClick={handleClose}
          size="small"
          sx={{ color: '#666' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={styles.form}>
          {generalError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
              {generalError}
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Nom"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            size="small"
            error={!!errors.name}
            helperText={errors.name}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />
        </DialogContent>

        <DialogActions sx={styles.actions}>
          <Button 
            onClick={handleClose}
            sx={styles.cancelButton}
          >
            Annuler
          </Button>
          <Button 
            type="submit"
            variant="contained"
            sx={styles.submitButton}
          >
            {itemData ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

Form.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  itemData: PropTypes.shape({
    name: PropTypes.string,
  })
};

export default Form;